import { useQuery } from "@tanstack/react-query";
import { getTestimonialsApiPublicTestimonialsGet } from "../api/generated";
import type { TestimonialResponse } from "../api/generated";

export interface Testimonial {
  id: string;
  client_name: string;
  client_title?: string | null;
  content: string;
  rating?: number | null;
  is_featured: boolean;
  is_published: boolean;
  photo_url?: string | null;
  display_order: number;
  created_at: string;
  updated_at: string;
}

/**
 * Hook for fetching published testimonials.
 */
export function useTestimonials() {
  return useQuery<Testimonial[]>({
    queryKey: ["testimonials"],
    queryFn: async (): Promise<Testimonial[]> => {
      const response = await getTestimonialsApiPublicTestimonialsGet();
      
      if (!response.data) {
        throw new Error("Failed to fetch testimonials");
      }
      
      // Transform TestimonialResponse[] to Testimonial[]
      // response.data is an array according to the API schema (status 200)
      const testimonialsData: TestimonialResponse[] = Array.isArray(response.data) 
        ? response.data 
        : [];
      
      return testimonialsData.map((t: TestimonialResponse): Testimonial => ({
        id: t.id,
        client_name: t.client_name,
        client_title: t.client_title,
        content: t.content,
        rating: t.rating,
        is_featured: t.is_featured ?? false,
        is_published: t.is_published ?? false,
        photo_url: t.photo_url,
        display_order: t.display_order ?? 0,
        created_at: t.created_at,
        updated_at: t.updated_at,
      }));
    },
    staleTime: 1000 * 60 * 10, // 10 minutes
  });
}
